import { createAnimationLegacy } from '../common/schemas/legacy/animation';
import { createExtendedPositionLegacy } from '../common/schemas/legacy/extended-position';
import { createShadowSettingsLegacy } from '../common/schemas/legacy/shadow';

import type { FigureEntity, LegacyFigureZero } from './schemas';

/**
 * Default zero-block data for a new figure — mirrors the host-app FormGroup
 * factory (`getZeroFigureWidgetData`): cyan 127×127 square, no border, no
 * shadow, inactive animation.
 */
const createZeroData = (): LegacyFigureZero['data'] => ({
    figure: 'square',
    backgroundColor: '#6FD2DD',
    keepProportions: false,
    isBorder: false,
    borderColor: '#000000',
    borderWidth: 2,
    borderStyle: 'solid',
    isShadow: false,
    // Mirror the legacy fixed `drop-shadow(0 0 5px rgba(14,14,14,.56))` look so
    // enabling the toggle on an existing square/round figure stays visually
    // close once it switches to a box-shadow.
    shadowSettings: createShadowSettingsLegacy({ blur: 5, spread: 0, color: 'rgba(14, 14, 14, 0.56)' }),
    animation: createAnimationLegacy(),
    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
    position: createExtendedPositionLegacy({
        width: 127,
        tabletWidth: 127,
        mobileWidth: 127,
        height: 127,
        tabletHeight: 127,
        mobileHeight: 127,
    }),
});

/**
 * Factory for the current legacy figure entity — matches the persisted widget
 * JSON shape: `{ id, name: 'figure', columnId, type: 'zero', data, … }`.
 * No variant argument: figure ships zero-block placement only, so the
 * envelope always carries the `type: 'zero'` tag.
 */
export function createLegacyFigure(): LegacyFigureZero {
    return {
        id: '',
        name: 'figure',
        columnId: '',
        type: 'zero',
        data: createZeroData(),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default
 * for new figure widgets. While only `legacy` exists, this delegates to
 * {@link createLegacyFigure}.
 */
export function createFigure(): FigureEntity {
    return createLegacyFigure();
}
